import { CommonModule, NgComponentOutlet } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PStepperHeaderComponent } from '../p-stepper-header/p-stepper-header.component';
import { StepperStep } from 'apps/perval/src/app/authentification/components/sign-up/interfaces/stepper-step.interface';
import { PButtonComponent } from '../../atoms/p-button/p-button.component';
import { StepperNavigationHelperService } from '../../../services/behavior/stepper-navigation-helper.service';
import { Subject, takeUntil } from 'rxjs';
import { IconsModule } from '../../icons/icons.module';
import { Router } from '@angular/router';

@Component({
  selector: 'p-stepper',
  standalone: true,
  imports: [
    CommonModule,
    NgComponentOutlet,
    PStepperHeaderComponent,
    PButtonComponent,
    IconsModule,
  ],
  templateUrl: './p-stepper.component.html',
  styleUrls: ['./p-stepper.component.scss'],
})
export class PStepperComponent implements OnInit, OnDestroy {
  constructor(
    private stepperNavigationHelper: StepperNavigationHelperService,
    private router: Router,
  ) {}

  @Input() steps: StepperStep[] = [];

  private readonly destroy$ = new Subject<void>();

  currentStepIndex = 0;
  disableNextButton = false;

  ngOnInit(): void {
    this.stepperNavigationHelper
      .getGoToStep()
      .pipe(takeUntil(this.destroy$))
      .subscribe((indexOfStepToGo: number) => {
        this.goToStep(indexOfStepToGo);
      });

    this.stepperNavigationHelper
      .getDisableGoToStep()
      .pipe(takeUntil(this.destroy$))
      .subscribe((disableGoToNextStep: boolean) => {
        this.disableNextButton = disableGoToNextStep;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get currentStep(): StepperStep | null {
    return this.steps[this.currentStepIndex] ?? null;
  }

  get canGoPrevious(): boolean {
    return this.currentStepIndex > 0;
  }

  get canGoNext(): boolean {
    if (!this.steps.length) {
      return false;
    }

    if (this.currentStepIndex >= this.steps.length - 1) {
      return false;
    }

    const nextStep = this.steps[this.currentStepIndex + 1];

    return !nextStep?.isDisabled;
  }

  next(): void {
    if (!this.canGoNext) {
      return;
    }

    this.currentStepIndex += 1;
  }

  previous(): void {
    if (!this.canGoPrevious) {
      return;
    }

    this.currentStepIndex -= 1;
  }

  goToStep(index: number): void {
    if (index < 0 || index >= this.steps.length) {
      return;
    }

    if (this.steps[index]?.isDisabled) {
      return;
    }

    this.currentStepIndex = index;
  }

  get stepLabels(): string[] {
    return this.steps.map((step) => step.label);
  }

  confirmed() {
    if (!this.currentStep?.isFinalStep) {
      return;
    }
    this.stepperNavigationHelper.emitFinalize(true);
  }
}
